/**
 * Cross-platform voice recording utility
 * Supports all modern browsers and devices
 */

export interface VoiceRecorderOptions {
  maxDuration?: number; // Maximum recording duration in seconds (default: 300 = 5 minutes)
  sampleRate?: number; // Sample rate in Hz (default: 48000)
  onDataAvailable?: (data: Blob) => void;
  onError?: (error: Error) => void;
  onStop?: (blob: Blob, duration: number) => void;
  onStart?: () => void;
  onAmplitudeUpdate?: (amplitude: number) => void; // For live waveform visualization
}

export class VoiceRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startTime: number = 0;
  private animationId: number | null = null;
  private options: Required<VoiceRecorderOptions>;
  private maxDurationTimeout: NodeJS.Timeout | null = null;

  constructor(options: VoiceRecorderOptions = {}) {
    this.options = {
      maxDuration: options.maxDuration ?? 300,
      sampleRate: options.sampleRate ?? 48000,
      onDataAvailable: options.onDataAvailable ?? (() => {}),
      onError: options.onError ?? ((error) => console.error('Voice recording error:', error)),
      onStop: options.onStop ?? (() => {}),
      onStart: options.onStart ?? (() => {}),
      onAmplitudeUpdate: options.onAmplitudeUpdate ?? (() => {}),
    };
  }

  /**
   * Check if recording is supported on this platform
   */
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    );
  }

  /**
   * Get the best supported MIME type for audio recording
   */
  static getSupportedMimeType(): string {
    const types = [
      'audio/mp4',
      'audio/mpeg',
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
    ];

    if (window.MediaRecorder) {
      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          return type;
        }
      }
    }

    return 'audio/mp4'; // Fallback
  }

  /**
   * Start recording audio
   */
  async start(): Promise<void> {
    try {
      // Check browser support
      if (!VoiceRecorder.isSupported()) {
        throw new Error('Voice recording is not supported on this browser');
      }

      // Request microphone permission
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: this.options.sampleRate,
          channelCount: 1, // Mono audio for smaller file size
        },
      });

      // Create MediaRecorder with the best supported format
      const mimeType = VoiceRecorder.getSupportedMimeType();
      const options: MediaRecorderOptions = {
        mimeType,
      };

      // Add bitrate for better compression on supported browsers
      if (mimeType.includes('webm') || mimeType.includes('opus')) {
        options.audioBitsPerSecond = 32000; // 32kbps for voice
      }

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.chunks = [];

      // Set up audio analysis for waveform visualization
      this.setupAudioAnalyser();

      // Handle data available event
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
          this.options.onDataAvailable(event.data);
        }
      };

      // Handle stop event
      this.mediaRecorder.onstop = () => {
        const duration = (Date.now() - this.startTime) / 1000;
        const blob = new Blob(this.chunks, { type: mimeType });
        this.options.onStop(blob, duration);
        this.cleanup();
      };

      // Handle error event
      this.mediaRecorder.onerror = (event) => {
        this.options.onError(new Error(`MediaRecorder error: ${event}`));
        this.cleanup();
      };

      // Start recording
      this.mediaRecorder.start(1000); // Collect data every second
      this.startTime = Date.now();
      this.options.onStart();

      // Set maximum duration timeout
      if (this.options.maxDuration > 0) {
        this.maxDurationTimeout = setTimeout(() => {
          this.stop();
        }, this.options.maxDuration * 1000);
      }

      // Start amplitude monitoring
      this.startAmplitudeMonitoring();
    } catch (error) {
      this.options.onError(error as Error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Stop recording
   */
  stop(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
    }
  }

  /**
   * Get current recording state
   */
  getState(): RecordingState | null {
    return this.mediaRecorder?.state ?? null;
  }

  /**
   * Get recording duration in seconds
   */
  getDuration(): number {
    if (!this.startTime) return 0;
    return (Date.now() - this.startTime) / 1000;
  }

  /**
   * Set up audio analyser for waveform visualization
   */
  private setupAudioAnalyser(): void {
    if (!this.stream) return;

    // Create audio context
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new AudioContextClass();

    // Create analyser node
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.8;

    // Connect stream to analyser
    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.source.connect(this.analyser);
  }

  /**
   * Start monitoring amplitude for visualization
   */
  private startAmplitudeMonitoring(): void {
    if (!this.analyser) return;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    const updateAmplitude = () => {
      if (!this.analyser) return;

      this.analyser.getByteFrequencyData(dataArray);

      // Calculate average amplitude
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const amplitude = sum / dataArray.length / 255; // Normalize to 0-1

      this.options.onAmplitudeUpdate(amplitude);

      // Continue animation
      if (this.mediaRecorder?.state === 'recording') {
        this.animationId = requestAnimationFrame(updateAmplitude);
      }
    };

    updateAmplitude();
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    // Stop animation
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Clear references
    this.mediaRecorder = null;
    this.analyser = null;
    this.source = null;
    this.chunks = [];
  }

  /**
   * Generate waveform data from audio blob
   */
  static async generateWaveform(
    blob: Blob,
    samples: number = 100
  ): Promise<number[]> {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const rawData = audioBuffer.getChannelData(0);
    const blockSize = Math.floor(rawData.length / samples);
    const waveform: number[] = [];

    for (let i = 0; i < samples; i++) {
      const start = blockSize * i;
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[start + j]);
      }
      waveform.push(sum / blockSize);
    }

    audioContext.close();

    // Normalize waveform
    const max = Math.max(...waveform);
    return waveform.map(v => v / max);
  }

  /**
   * Convert audio blob to base64
   */
  static async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}